import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ProductCard } from "@/components/ProductCard";
import { fetchProducts, fetchCollections } from "@/lib/api";

export default function Shop() {
  const { slug } = useParams();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    fetchCollections().then(setCollections).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts(slug)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [slug]);

  const activeCollection = useMemo(
    () => collections.find((c) => c.slug === slug),
    [collections, slug]
  );

  const sorted = useMemo(() => {
    // Expand each product into per-color cards (variety view)
    const expanded = [];
    for (const p of products) {
      if (p.colors && p.colors.length > 0 && p.color_images) {
        for (const c of p.colors) {
          expanded.push({
            ...p,
            // Use a unique slug per variant for the card key
            id: `${p.id}__${c}`,
            slug: p.slug, // detail page is the parent product
            color: c,
            colors: [c], // show only this color's swatch
            image: p.color_images[c] || p.image,
            // Append color to display name
            name: `${p.name.replace(/ \(.*\)$/, '')} — ${c}`,
            _initialColor: c,
          });
        }
      } else {
        expanded.push(p);
      }
    }
    const list = [...expanded];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, sort]);

  const heading = activeCollection?.name || "Shop All";
  const subhead = activeCollection?.tagline || "The full Boat 2 Tee lineup";
  const heroImg =
    activeCollection?.image ||
    "https://images.pexels.com/photos/21550490/pexels-photo-21550490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

  return (
    <div data-testid="shop-page">
      {/* Collection hero */}
      <section className="relative h-[44vh] min-h-[320px] overflow-hidden">
        <img src={heroImg} alt={heading} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-navy/30" />
        <div className="relative z-10 h-full flex items-end max-w-7xl mx-auto px-6 md:px-12 pb-12 text-white">
          <div>
            <p className="b2t-overline !text-seafoam-400 mb-3" data-testid="shop-subhead">{subhead}</p>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight" data-testid="shop-heading">{heading}</h1>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <div className="sticky top-[88px] z-30 bg-white/85 backdrop-blur-xl border-b border-navy/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Link
              to="/shop"
              data-testid="filter-all"
              className={`whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.18em] px-3.5 py-2 border transition-colors ${
                !slug
                  ? "bg-navy text-white border-navy"
                  : "border-navy/20 text-navy hover:border-navy"
              }`}
            >
              All
            </Link>
            {collections.map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                data-testid={`filter-${c.slug}`}
                className={`whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.18em] px-3.5 py-2 border transition-colors ${
                  slug === c.slug
                    ? "bg-navy text-white border-navy"
                    : "border-navy/20 text-navy hover:border-navy"
                }`}
              >
                {c.name.replace("Performance ", "").replace("Coastal ", "")}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-navy/50 uppercase tracking-widest">Sort</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="border border-navy/20 bg-white py-2 px-3 text-xs font-semibold uppercase tracking-widest text-navy focus:outline-none focus:border-navy"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">A-Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* Product grid */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-sand animate-pulse" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-navy/60">No products in this collection yet. Check back soon.</p>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-widest text-navy/40 mb-8" data-testid="product-count">
                {sorted.length} {sorted.length === 1 ? "product" : "products"}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {sorted.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
